import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/pages/AuthShell";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const navigate = useNavigate();
  const { loginWith } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWith(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
      navigate(data.user.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Log in to open your command center."
      footer={<>New here? <Link to="/register" className="text-primary font-semibold hover:underline">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-slate-600">Email</Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input" placeholder="teacher@demo.com"
              className="pl-10 rounded-xl bg-slate-50 border-slate-200 h-12 focus:bg-white" />
          </div>
        </div>
        <div>
          <Label htmlFor="password" className="text-slate-600">Password</Label>
          <div className="relative mt-1.5">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input" placeholder="••••••••"
              className="pl-10 rounded-xl bg-slate-50 border-slate-200 h-12 focus:bg-white" />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit-button"
          className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base">
          {loading ? <Loader2 className="animate-spin" /> : "Log in"}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-6">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-xs text-slate-400">or</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label="Continue with Google" />
    </AuthShell>
  );
}
